import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import Logger from '../utils/Logger';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadingRef = React.useRef(true);
    const [hasError, setHasError] = useState(null);
    const [invites, setInvites] = useState([]); // State to hold invites

    useEffect(() => {
        // Set Firebase persistence once, but only on web or if needed for native
        if (Capacitor.getPlatform() === 'web') {
            setPersistence(auth, browserLocalPersistence)
                .catch(err => Logger.error("AuthContext", "Failed to set persistence", err));
        }
    }, []);

    useEffect(() => {
        // Single Firebase auth listener
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            Logger.info("AuthContext", "Auth state changed", {
                status: authUser ? "Logged In" : "Logged Out",
                uid: authUser?.uid
            });
            setUser(authUser);
            if (!authUser) {
                setHasError("User logged out");
            } else {
                setHasError(null); // Clear error when user logs in

                // Ensure the user exists in Data Connect and record the login time
                import('../dataconnect-generated/js/default-connector').then(module => {
                    // Only upsert email/photo — never overwrite name on login.
                    // Name is managed by the user via OnboardingDialog / profile settings.
                    const upsertData = {
                        email: authUser.email || "",
                        photo: authUser.photoURL || ""
                    };
                    module.upsertUser(upsertData).catch(err => {
                        Logger.warn("AuthContext", "Failed to upsert user on login", err);
                    });
                });

                // Initialize push notifications on native devices
                if (Capacitor.getPlatform() !== 'web') {
                    import('../services/PushNotificationService').then(module => {
                        module.initializePushNotifications();
                    }).catch(err => Logger.error("AuthContext", "Failed to load PushNotificationService", err));
                }
            }
            setLoading(false);
            loadingRef.current = false;
        });

        // Safety timeout in case auth listener hangs
        const timeout = setTimeout(() => {
            if (loadingRef.current) {
                Logger.warn("AuthContext", "Auth check timed out, forcing loading=false");
                setLoading(false);
                loadingRef.current = false;
            }
        }, 10000);

        return () => {
            unsubscribe();
            clearTimeout(timeout);
        };
    }, []);

    const value = {
        user,
        loading,
        hasError,
        invites,
        setInvites,
        setHasError
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};